import React, { useEffect, useMemo, useState } from 'react';
import { fromIso } from '../../utils/businessDate';
import { getTenantToday } from '../../utils/dateUtils';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import styled, { createGlobalStyle } from 'styled-components';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import {
  PortcoGlobal,
  Surface,
  Header,
  HeaderInner,
  HeaderBrand,
  HeaderBrandMark,
  HeaderCrumb,
  Body,
  ContextStrip,
  ContextStripInner,
  ContextName,
  ContextNameTitle,
  ContextNameSub,
  ContextField,
  ContextFieldLabel,
  ContextFieldValue,
  TabBar,
  TabBarInner,
  Tab,
  StatStrip,
  StatCell,
  StatLabel,
  StatValue,
  StatSub,
  Panel,
  PanelHead,
  PanelTitle,
  PanelMeta,
  PanelBody,
  Table,
  Thead,
  Th,
  Td,
  TdMono,
  Tr,
  Loading,
  Empty,
  portcoTokens,
} from '../portco/PortcoTheme';
import SubscriptionLineTab from '../alternatives/SubscriptionLineTab';

import { DateField } from '../shared/DateField';

/**
 * LP Fund Performance Detail — Aladdin-tier single-fund screen.
 *
 * Powered by:
 *   GET /api/pe/performance/funds/{fundId}      — metric stack
 *   GET /api/alternatives/phase3/funds/{id}/j-curve  — J-curve buckets
 *
 * Tabs:
 *   Overview  — metric stack + J-curve chart + capital deployment summary
 *   Cash flows— contribution/distribution timeline (monthly buckets)
 *   Benchmark — KS-PME, Direct Alpha, benchmark IRR comparison panel
 *
 * Visual contract: PortcoTheme (PGIM-tier light institutional). Charts use
 * recharts and inherit token colors so the page reads as a single design.
 */

interface Metrics {
  scope: string;
  scopeId: number | null;
  label: string;
  fundType?: string | null;
  vintageYear?: number | null;
  manager?: string | null;
  strategy?: string | null;
  fundCount?: number | null;
  committed: number;
  called: number;
  distributed: number;
  reportedNav: number;
  totalValue: number;
  unfunded: number;
  irr?: number | null;
  moic?: number | null;
  tvpi?: number | null;
  dpi?: number | null;
  rvpi?: number | null;
  ksPme?: number | null;
  directAlpha?: number | null;
  benchmarkIRR?: number | null;
  // Phase A1 — extended PME suite
  longNickelsPme?: number | null;   // alias for benchmarkIRR (Long-Nickels 1996)
  pmePlus?: number | null;          // PME+ IRR (Capital Dynamics, Rouvinez 2003)
  pmePlusLambda?: number | null;    // λ scaling factor (diagnostic)
  cambridgeMPme?: number | null;    // Cambridge mPME multiple — null until NAV history available
  dataSource?: string | null;       // benchmark provenance: POLYGON_*, PROXY_*, *_OFFICIAL
  benchmarkCode?: string;
  navAsOf?: string | null;
  firstFlowDate?: string | null;
  lastFlowDate?: string | null;
  cashFlowCount?: number | null;
}

interface JBucket {
  month: string;
  monthEnd: string;
  cumulativeCalled: number;
  cumulativeDistributed: number;
  reportedNav: number;
  totalValue: number;
  netCashFlow: number;
  unrealizedPnl: number;
  dpi: number;
  rvpi: number;
  tvpi: number;
}

interface JCurvePayload {
  scope: string;
  fundId: number;
  fundName?: string;
  vintageYear?: number;
  commitmentCount?: number;
  totalCommitted?: number;
  latestNav?: number;
  latestNavDate?: string | null;
  buckets: JBucket[];
  summary?: Record<string, any>;
}

// ---------- ILPA helpers ----------
function defaultQuarterEnd(): string {
  // Most recently completed quarter end as YYYY-MM-DD.
  const now = fromIso(getTenantToday());
  const m = now.getMonth(); // 0..11
  const y = now.getFullYear();
  // Completed quarter ends: Mar 31, Jun 30, Sep 30, Dec 31.
  const lastQ = Math.floor(m / 3) - 1; // -1 means prior year Q4
  if (lastQ < 0) {
    return `${y - 1}-12-31`;
  }
  const month = (lastQ + 1) * 3; // 3, 6, 9
  const day = month === 6 ? 30 : month === 9 ? 30 : month === 3 ? 31 : 31;
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}

// ---------- formatters ----------
const fmtMoney = (v?: number | null) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};
const fmtPct = (v?: number | null, d = 1) => (v == null ? '—' : `${(v * 100).toFixed(d)}%`);
const fmtMult = (v?: number | null) => (v == null ? '—' : `${v.toFixed(2)}x`);
const fmtDate = (iso?: string | null) => (iso ? iso : '—');
// Bi-temporal stamp: "Jul 15, 2025". Open-ended knowledge rows (9999 sentinel)
// render as "Open" — i.e. still the current knowledge state.
const fmtStamp = (ts?: string | null) => {
  if (!ts) return '—';
  // Open-ended sentinel is 9999-12-31, but TZ serialization can roll it into
  // year 10000 (e.g. "+10000-01-01T..."). Catch any year >= 9999 as "Open".
  const yearMatch = ts.match(/^\+?(\d{4,})/);
  if (yearMatch && Number(yearMatch[1]) >= 9999) return 'Open';
  const d = new Date(ts.replace(' ', 'T'));
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

/**
 * Compute fund lifecycle stage from vintage year and capital deployment state.
 * Institutional convention:
 *   - Years 0–5 from vintage: Investment Period
 *   - Years 5–8: Harvest (no new investments, some realisations)
 *   - Years 8+: Wind-down (recycling / final realisations)
 * Override: distributed > called → Harvest regardless of age.
 */
const fundStage = (vintage?: number | null, called?: number, distributed?: number): {
  label: string;
  tone: 'pos' | 'neg' | 'neutral';
} => {
  if (vintage == null) return { label: 'Status n/a', tone: 'neutral' };
  const now = fromIso(getTenantToday()).getFullYear();
  const age = now - vintage;
  if (distributed != null && called != null && called > 0 && distributed >= called) {
    return { label: 'Harvest', tone: 'pos' };
  }
  if (age <= 5) return { label: 'Investment period', tone: 'neutral' };
  if (age <= 8) return { label: 'Harvest', tone: 'pos' };
  return { label: 'Wind-down', tone: 'neutral' };
};

/** Years/months elapsed since vintage — for empty-state context. */
const ageFromVintage = (vintage?: number | null): string => {
  if (vintage == null) return '';
  const yrs = fromIso(getTenantToday()).getFullYear() - vintage;
  if (yrs <= 0) return 'pre-close';
  if (yrs === 1) return '~1 yr in';
  return `${yrs} yrs in`;
};
const monthLabel = (ym: string) => {
  // Recharts axis: render YYYY-MM as 'MMM YY'
  const [y, m] = ym.split('-');
  if (!y || !m) return ym;
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleString(undefined, { month: 'short', year: '2-digit' });
};

// ---------- styled local helpers ----------
const ChartFrame = styled.div`
  width: 100%;
  height: 360px;
  padding: 16px 4px 4px 0;
`;
const KpiSplit = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const Pill = styled.span<{ $tone: 'pos' | 'neg' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  background: ${(p) =>
    p.$tone === 'pos'
      ? portcoTokens.statusPassingSubtle
      : p.$tone === 'neg'
      ? portcoTokens.statusBreachedSubtle
      : portcoTokens.surfaceMuted};
  color: ${(p) =>
    p.$tone === 'pos'
      ? portcoTokens.statusPassing
      : p.$tone === 'neg'
      ? portcoTokens.statusBreached
      : portcoTokens.inkSubtle};
`;
const InsightRow = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid ${portcoTokens.hairline};
  &:last-child {
    border-bottom: none;
  }
`;
const InsightLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.06em;
  color: ${portcoTokens.inkSubtle};
  text-transform: uppercase;
`;
const InsightBody = styled.div`
  font-size: 13px;
  color: ${portcoTokens.inkBody};
  line-height: 1.55;
`;
const BackLink = styled.button`
  background: none;
  border: none;
  color: ${portcoTokens.accent};
  font-size: 12px;
  letter-spacing: 0.04em;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  &:hover {
    text-decoration: underline;
  }
`;

type TabKey = 'overview' | 'cashflows' | 'benchmark' | 'secondary' | 'subline' | 'reports';

// ---------- Secondary pricing types ----------
interface PriceBand {
  fundId: number;
  fundName: string | null;
  asOfDate: string | null;
  reportedNav: number | null;
  calledCapital: number | null;
  distributedCapital: number | null;
  unfundedCommitment: number | null;
  residualMonths: number;
  investmentMonthsRemaining: number;
  targetMoicRemaining: number | null;
  discountRateLowPct: number | null;
  discountRateMidPct: number | null;
  discountRateHighPct: number | null;
  bidLow: number | null;
  bidMid: number | null;
  bidHigh: number | null;
  bidLowPctOfNav: number | null;
  bidMidPctOfNav: number | null;
  bidHighPctOfNav: number | null;
}

// ---------- ILPA report types ----------
interface ILPAReportListRow {
  id: number;
  quarter_end: string;
  fiscal_period: string;
  generated_at?: string;
  generated_by?: string;
  knowledge_from?: string;
  knowledge_to?: string;
  irr?: number | null;
  tvpi?: number | null;
  dpi?: number | null;
  rvpi?: number | null;
  ks_pme?: number | null;
  data_source?: string | null;
}

interface ILPAGenerateResult {
  reportId: number;
  fundId: number;
  quarterEnd: string;
  fiscalPeriod: string;
  report: Record<string, any>;
}

const SUB_TEMPLATES: { key: string; label: string; rationale: string }[] = [
  { key: 'capitalAccount', label: 'Capital account', rationale: 'Quarterly LP capital account roll-forward (commits, calls, distributions, NAV).' },
  { key: 'scheduleOfInvestments', label: 'Schedule of investments', rationale: 'Underlying portfolio company holdings, cost basis, fair value, ownership %.' },
  { key: 'feesAndExpenses', label: 'Fees & expenses', rationale: 'ILPA-prescribed fee/expense template — mgmt fees, partnership expenses, offsets.' },
  { key: 'cashFlow', label: 'Cash flow', rationale: 'Period and inception-to-date contributions, distributions, recallable detail.' },
  { key: 'performance', label: 'Performance', rationale: 'IRR, TVPI/DPI/RVPI, KS-PME, Direct Alpha (standardized cells).' },
  { key: 'managementFeeOffset', label: 'Mgmt-fee offset', rationale: 'Transaction/monitoring fees received and offset against mgmt fee.' },
  { key: 'gpLedSecondary', label: 'GP-led secondary', rationale: 'Continuation-fund / GP-led transaction disclosure (if any).' },
];

// ---------- styled helpers (Reports tab) ----------
const ReportsLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
// Filings table can exceed the panel width on narrow viewports — let it scroll
// horizontally rather than overflow the card.
const FilingsTableWrap = styled.div`
  min-width: 0;
  overflow-x: auto;
`;
const GenForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid ${portcoTokens.hairline};
  border-radius: 6px;
  background: ${portcoTokens.surfaceMuted};
  margin-bottom: 16px;
`;
const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: ${portcoTokens.inkSubtle};
  text-transform: uppercase;
`;
const DateInput = styled.input`
  font-family: ${portcoTokens.fontSans};
  font-size: 13px;
  padding: 6px 10px;
  border: 1px solid ${portcoTokens.hairline};
  border-radius: 4px;
  background: #ffffff;
  color: ${portcoTokens.inkPrimary};
`;
const GenerateButton = styled.button`
  font-family: ${portcoTokens.fontSans};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 8px 16px;
  border: 1px solid ${portcoTokens.accent};
  background: ${portcoTokens.accent};
  color: #ffffff;
  border-radius: 4px;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const ReportRow = styled.tr<{ $active?: boolean }>`
  cursor: pointer;
  background: ${(p) => (p.$active ? portcoTokens.surfaceMuted : 'transparent')};
  &:hover {
    background: ${portcoTokens.surfaceMuted};
  }
`;
const SubTplGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;
const SubTplCard = styled.div`
  border: 1px solid ${portcoTokens.hairline};
  border-radius: 6px;
  padding: 14px 16px;
  background: ${portcoTokens.surfaceTile};
`;
const SubTplLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  margin-bottom: 6px;
`;
const SubTplCount = styled.div`
  font-family: ${portcoTokens.fontMono};
  font-size: 18px;
  color: ${portcoTokens.inkPrimary};
  font-weight: 600;
  margin-bottom: 4px;
`;
const SubTplRationale = styled.div`
  font-size: 11px;
  color: ${portcoTokens.inkSubtle};
  line-height: 1.5;
`;
const ErrorBanner = styled.div`
  padding: 10px 14px;
  margin-bottom: 12px;
  border: 1px solid ${portcoTokens.statusBreachedSubtle};
  background: ${portcoTokens.statusBreachedSubtle};
  color: ${portcoTokens.statusBreached};
  font-size: 12px;
  border-radius: 4px;
`;

// ---------- styled helpers (Secondary tab) ----------
const BidGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const BidCard = styled.div<{ $tone: 'low' | 'mid' | 'high' }>`
  border: 1px solid ${(p) =>
    p.$tone === 'low'
      ? portcoTokens.statusPassing
      : p.$tone === 'high'
      ? portcoTokens.statusBreached
      : portcoTokens.hairline};
  background: ${(p) =>
    p.$tone === 'low'
      ? portcoTokens.statusPassingSubtle
      : p.$tone === 'high'
      ? portcoTokens.statusBreachedSubtle
      : portcoTokens.surfaceTile};
  border-radius: 6px;
  padding: 16px 18px;
`;
const BidLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  margin-bottom: 8px;
`;
const BidValue = styled.div`
  font-family: ${portcoTokens.fontMono};
  font-size: 22px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
`;
const BidPct = styled.div`
  font-family: ${portcoTokens.fontMono};
  font-size: 13px;
  color: ${portcoTokens.inkSubtle};
  margin-top: 4px;
`;
const BidRate = styled.div`
  font-size: 11px;
  letter-spacing: 0.04em;
  color: ${portcoTokens.inkSubtle};
  margin-top: 12px;
  border-top: 1px solid ${portcoTokens.hairline};
  padding-top: 8px;
`;
const AssumptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 16px;
`;
const AssumptionCell = styled.div`
  border: 1px solid ${portcoTokens.hairline};
  border-radius: 6px;
  padding: 12px 14px;
  background: ${portcoTokens.surfaceMuted};
`;
const AssumptionLabel = styled.div`
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  margin-bottom: 6px;
`;
const AssumptionValue = styled.div`
  font-family: ${portcoTokens.fontMono};
  font-size: 14px;
  color: ${portcoTokens.inkPrimary};
  font-weight: 600;
`;
const ScopeNote = styled.div`
  font-size: 11px;
  color: ${portcoTokens.inkSubtle};
  line-height: 1.55;
  margin-top: 16px;
  padding: 10px 14px;
  border: 1px dashed ${portcoTokens.hairline};
  background: ${portcoTokens.surfaceMuted};
  border-radius: 4px;
`;

/* ---------- Embedded-mode tearsheet shell ----------
   When the detail screen is hosted INSIDE the Alternatives workbench, we
   render a slim breadcrumb bar (dark) sitting on the workbench, and the
   light tearsheet directly under it — no duplicate AxiomFlow brand row.
   Aladdin/Eikon convention: dark workspace chrome wraps a light "document"
   panel; the breadcrumb is the bridge that keeps the user oriented. */
const EmbeddedShell = styled.div`
  background: #ffffff;
  color: ${portcoTokens.inkBody};
  font-family: ${portcoTokens.fontSans};
  font-feature-settings: 'tnum' 1, 'ss01' 1;
`;

const EmbeddedCrumbBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 24px;
  background: rgba(15, 23, 42, 0.55);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
  font-size: 12px;
  letter-spacing: 0.04em;
`;

const CrumbTrail = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.08em;
  & strong { color: #e2e8f0; font-weight: 600; }
  & .sep { color: #475569; }
`;

const CrumbBack = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cbd5e1;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-family: inherit;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: rgba(147, 197, 253, 0.5);
    color: #bfdbfe;
    background: rgba(59, 130, 246, 0.08);
  }
`;

const CrumbMeta = styled.span`
  color: #94a3b8;
  font-size: 11px;
  letter-spacing: 0.04em;
  font-family: ${portcoTokens.fontMono};
`;

const EmbeddedBody = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 28px 32px 56px;
`;

/* ---------- Refined hero (tearsheet header) ----------
   Two-column institutional pattern:
   left = name / strategy / status chip;
   right = vintage / manager / committed / NAV-as-of in a tight key-value grid.
   Replaces the flat ContextStrip in embedded mode. */
const HeroBlock = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 40px;
  padding: 24px 0 28px;
  border-bottom: 1px solid ${portcoTokens.hairline};
  margin-bottom: 28px;
  @media (max-width: 920px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeroEyebrow = styled.div`
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
`;

const HeroTitle = styled.h1`
  font-size: 26px;
  line-height: 1.15;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${portcoTokens.inkPrimary};
  margin: 0;
`;

const HeroSubtitle = styled.div`
  font-size: 13px;
  color: ${portcoTokens.inkMuted};
  letter-spacing: 0.01em;
  & .sep { color: ${portcoTokens.hairlineStrong}; margin: 0 8px; }
`;

const HeroChipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const StageChip = styled.span<{ $tone: 'pos' | 'neg' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$tone === 'pos'
      ? portcoTokens.statusPassingSubtle
      : p.$tone === 'neg'
      ? portcoTokens.statusBreachedSubtle
      : portcoTokens.surfaceMuted};
  color: ${(p) =>
    p.$tone === 'pos'
      ? portcoTokens.statusPassing
      : p.$tone === 'neg'
      ? portcoTokens.statusBreached
      : portcoTokens.inkMuted};
  border: 1px solid ${(p) =>
    p.$tone === 'pos'
      ? portcoTokens.statusPassing
      : p.$tone === 'neg'
      ? portcoTokens.statusBreached
      : portcoTokens.hairline};
  border-color: ${(p) => (p.$tone === 'neutral' ? portcoTokens.hairlineStrong : 'currentColor')};
`;

const ClassChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${portcoTokens.accentSubtle};
  color: ${portcoTokens.accent};
  border: 1px solid ${portcoTokens.accent};
`;

const HeroRight = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  row-gap: 10px;
  column-gap: 20px;
  margin: 0;
  align-content: start;
`;

const HeroDt = styled.dt`
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  align-self: center;
`;

const HeroDd = styled.dd`
  margin: 0;
  font-size: 13px;
  color: ${portcoTokens.inkPrimary};
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  font-weight: 500;
  text-align: right;
`;

/* Tabular nums applied to the StatStrip values + all mono cells in this screen. */
// Apply tabular numerals across the entire tearsheet so percent, multiple,
// and currency columns line up consistently. The wrapper class is set on
// both the standalone Surface and the embedded shell.
const TabularStyle = createGlobalStyle`
  .lp-fund-tearsheet,
  .lp-fund-tearsheet * {
    font-variant-numeric: tabular-nums;
    font-feature-settings: 'tnum' 1, 'lnum' 1;
  }
`;

interface LPFundDetailProps {
  embedded?: boolean;
  fundIdOverride?: string;
}

const LPFundPerformanceDetailScreen: React.FC<LPFundDetailProps> = ({ embedded = false, fundIdOverride }) => {
  const params = useParams<{ fundId: string }>();
  const fundId = fundIdOverride ?? params.fundId;
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [jCurve, setJCurve] = useState<JCurvePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('overview');

  // ILPA report state (lazy-loaded when Reports tab is opened)
  const [ilpaReports, setIlpaReports] = useState<ILPAReportListRow[] | null>(null);
  const [ilpaLoading, setIlpaLoading] = useState(false);
  const [ilpaError, setIlpaError] = useState<string | null>(null);
  const [ilpaSelectedId, setIlpaSelectedId] = useState<number | null>(null);
  const [ilpaSelectedReport, setIlpaSelectedReport] = useState<Record<string, any> | null>(null);
  const [ilpaQuarterEnd, setIlpaQuarterEnd] = useState<string>(() => defaultQuarterEnd());
  const [ilpaGenerating, setIlpaGenerating] = useState(false);

  // Secondary-pricing state (lazy-loaded when Secondary tab is opened)
  const [priceBand, setPriceBand] = useState<PriceBand | null>(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);
  // Empty string = use service default (12%); user may type their own.
  const [discountInput, setDiscountInput] = useState<string>('');

  useEffect(() => {
    if (!fundId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [m, j] = await Promise.all([
          axios.get<Metrics>(`/api/pe/performance/funds/${fundId}`),
          axios.get<JCurvePayload>(`/api/alternatives/phase3/funds/${fundId}/j-curve`),
        ]);
        if (cancelled) return;
        setMetrics(m.data);
        setJCurve(j.data);
      } catch (e: any) {
        if (!cancelled) {
          if (e?.response?.status === 404) {
            setError('Fund not found.');
          } else {
            setError(e?.response?.data?.message || e?.message || 'Failed to load.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fundId]);

  // Lazy-load ILPA report list when Reports tab is first opened.
  useEffect(() => {
    if (tab !== 'reports' || !fundId || ilpaReports !== null) return;
    let cancelled = false;
    (async () => {
      setIlpaLoading(true);
      setIlpaError(null);
      try {
        const r = await axios.get<ILPAReportListRow[]>(`/api/regulatory/ilpa/funds/${fundId}/reports`);
        if (cancelled) return;
        setIlpaReports(r.data ?? []);
      } catch (e: any) {
        if (!cancelled) {
          setIlpaError(e?.response?.data?.message || e?.message || 'Failed to load ILPA reports.');
          setIlpaReports([]);
        }
      } finally {
        if (!cancelled) setIlpaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, fundId, ilpaReports]);

  const openIlpaReport = async (reportId: number) => {
    if (ilpaSelectedId === reportId) {
      // Toggle off — clicking the selected filing again returns to the empty state.
      setIlpaSelectedId(null);
      setIlpaSelectedReport(null);
      return;
    }
    setIlpaSelectedId(reportId);
    setIlpaSelectedReport(null);
    try {
      const r = await axios.get<Record<string, any>>(`/api/regulatory/ilpa/reports/${reportId}`);
      const body = r.data?.report ?? r.data;
      setIlpaSelectedReport(body ?? {});
    } catch (e: any) {
      setIlpaError(e?.response?.data?.message || e?.message || 'Failed to load report detail.');
    }
  };

  const fetchPriceBand = async (overrideDiscountPct?: string) => {
    if (!fundId) return;
    setPriceLoading(true);
    setPriceError(null);
    try {
      const params: string[] = [];
      const useDiscount = overrideDiscountPct ?? discountInput;
      if (useDiscount.trim() !== '') {
        const n = Number(useDiscount);
        if (!Number.isFinite(n) || n < 0 || n > 100) {
          setPriceError('Market discount rate must be a number between 0 and 100.');
          setPriceLoading(false);
          return;
        }
        params.push(`marketDiscountRatePct=${n}`);
      }
      const qs = params.length ? `?${params.join('&')}` : '';
      const r = await axios.get<PriceBand>(`/api/alternatives/secondary-pricing/funds/${fundId}/price${qs}`);
      setPriceBand(r.data);
    } catch (e: any) {
      setPriceError(e?.response?.data?.message || e?.message || 'Failed to price secondary.');
    } finally {
      setPriceLoading(false);
    }
  };

  // Lazy-load the secondary price band when the Secondary tab is first opened.
  useEffect(() => {
    if (tab !== 'secondary' || !fundId || priceBand !== null) return;
    fetchPriceBand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fundId]);

  const generateIlpaReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fundId || !ilpaQuarterEnd) return;
    setIlpaGenerating(true);
    setIlpaError(null);
    try {
      const r = await axios.post<ILPAGenerateResult>(
        `/api/regulatory/ilpa/${fundId}/quarterly?quarterEnd=${ilpaQuarterEnd}`,
      );
      // refresh list
      const list = await axios.get<ILPAReportListRow[]>(`/api/regulatory/ilpa/funds/${fundId}/reports`);
      setIlpaReports(list.data ?? []);
      setIlpaSelectedId(r.data.reportId);
      setIlpaSelectedReport(r.data.report ?? {});
    } catch (err: any) {
      setIlpaError(err?.response?.data?.message || err?.message || 'Generation failed.');
    } finally {
      setIlpaGenerating(false);
    }
  };

  // Build flow-event series from the J-curve buckets (cumulative → period-over-period).
  const cashFlowEvents = useMemo(() => {
    if (!jCurve?.buckets) return [];
    let prevCalled = 0;
    let prevDistributed = 0;
    return jCurve.buckets.map((b) => {
      const periodCalled = b.cumulativeCalled - prevCalled;
      const periodDist = b.cumulativeDistributed - prevDistributed;
      prevCalled = b.cumulativeCalled;
      prevDistributed = b.cumulativeDistributed;
      return {
        month: b.month,
        called: -periodCalled, // negative = LP outflow (sticks down)
        distributed: periodDist,
        net: periodDist - periodCalled,
      };
    });
  }, [jCurve]);

  const buckets = jCurve?.buckets ?? [];
  const lastBucket = buckets.length > 0 ? buckets[buckets.length - 1] : null;

  const irrTone: 'pos' | 'neg' | 'neutral' =
    metrics?.irr == null ? 'neutral' : metrics.irr >= 0 ? 'pos' : 'neg';
  const alphaTone: 'pos' | 'neg' | 'neutral' =
    metrics?.directAlpha == null
      ? 'neutral'
      : metrics.directAlpha >= 0
      ? 'pos'
      : 'neg';
  const ksPmeTone: 'pos' | 'neg' | 'neutral' =
    metrics?.ksPme == null ? 'neutral' : metrics.ksPme >= 1.0 ? 'pos' : 'neg';

  // ---------- Hero data derivations ----------
  const stage = fundStage(metrics?.vintageYear, metrics?.called, metrics?.distributed);
  const age = ageFromVintage(metrics?.vintageYear);
  const noFlows = (metrics?.cashFlowCount ?? 0) === 0;
  // Empty-state copy: replace bare "—" with analyst-grade context so the
  // tearsheet doesn't read as half-built. Convention: "n/a · why" or
  // "awaiting first call · vintage 2023".
  const irrCopy =
    metrics?.irr != null
      ? fmtPct(metrics.irr)
      : noFlows
      ? 'n/a · awaiting first cash flow'
      : '—';
  const tvpiCopy =
    (metrics?.tvpi ?? metrics?.moic) != null
      ? fmtMult(metrics?.tvpi ?? metrics?.moic)
      : noFlows
      ? 'n/a · pre-deployment'
      : '—';
  const ksPmeCopy =
    metrics?.ksPme != null
      ? fmtMult(metrics.ksPme)
      : noFlows
      ? 'n/a · no flows to benchmark'
      : '—';
  const alphaCopy =
    metrics?.directAlpha != null
      ? fmtPct(metrics.directAlpha)
      : noFlows
      ? 'n/a · no flows'
      : '—';

  /* ---------- Body content (shared between standalone + embedded) ---------- */
  const tearsheetBody = (
    <>
      {loading && <Loading>Loading fund performance…</Loading>}
      {error && !loading && <Empty>{error}</Empty>}
      {!loading && !error && metrics && (
        <>
          {/* Refined hero: name + stage chip on the left, vintage/manager/
              committed/NAV-as-of as a tight key-value grid on the right. */}
          <HeroBlock>
            <HeroLeft>
              <HeroEyebrow>
                {embedded ? 'GP Workbench · LP Performance' : 'LP Performance'}
              </HeroEyebrow>
              <HeroTitle>{metrics.label}</HeroTitle>
              <HeroSubtitle>
                {metrics.manager ?? 'Manager n/a'}
                {metrics.strategy ? <><span className="sep">·</span>{metrics.strategy}</> : null}
                {metrics.vintageYear ? <><span className="sep">·</span>Vintage {metrics.vintageYear}{age ? ` (${age})` : ''}</> : null}
              </HeroSubtitle>
              <HeroChipRow>
                {metrics.fundType && (
                  <ClassChip>{metrics.fundType.replace(/_/g, ' ')}</ClassChip>
                )}
                <StageChip $tone={stage.tone}>{stage.label}</StageChip>
              </HeroChipRow>
            </HeroLeft>
            <HeroRight>
              <HeroDt>Committed</HeroDt>
              <HeroDd>{fmtMoney(metrics.committed)}</HeroDd>
              <HeroDt>Called</HeroDt>
              <HeroDd>{fmtMoney(metrics.called)}</HeroDd>
              <HeroDt>Distributed</HeroDt>
              <HeroDd>{fmtMoney(metrics.distributed)}</HeroDd>
              <HeroDt>Reported NAV</HeroDt>
              <HeroDd>{fmtMoney(metrics.reportedNav)}</HeroDd>
              <HeroDt>NAV as of</HeroDt>
              <HeroDd>{metrics.navAsOf ?? 'no NAV reported'}</HeroDd>
              <HeroDt>Cash-flow events</HeroDt>
              <HeroDd>
                {metrics.cashFlowCount ?? 0}
                {noFlows && age ? <span style={{ color: portcoTokens.inkSubtle, fontWeight: 400 }}> · {age}</span> : null}
              </HeroDd>
            </HeroRight>
          </HeroBlock>

          <TabBar>
                <TabBarInner>
                  <Tab $active={tab === 'overview'} onClick={() => setTab('overview')}>
                    Overview
                  </Tab>
                  <Tab $active={tab === 'cashflows'} onClick={() => setTab('cashflows')}>
                    Cash flows
                  </Tab>
                  <Tab $active={tab === 'benchmark'} onClick={() => setTab('benchmark')}>
                    Benchmark
                  </Tab>
                  <Tab $active={tab === 'secondary'} onClick={() => setTab('secondary')}>
                    Secondary
                  </Tab>
                  <Tab $active={tab === 'subline'} onClick={() => setTab('subline')}>
                    Sub-line
                  </Tab>
                  <Tab $active={tab === 'reports'} onClick={() => setTab('reports')}>
                    Reports
                  </Tab>
                </TabBarInner>
              </TabBar>

              <StatStrip>
                <StatCell>
                  <StatLabel>Net IRR</StatLabel>
                  <StatValue>
                    {irrCopy}
                    {metrics.irr != null && (
                      <Pill $tone={irrTone}>{irrTone === 'pos' ? '▲' : irrTone === 'neg' ? '▼' : '•'}</Pill>
                    )}
                  </StatValue>
                  <StatSub>since-inception, money-weighted</StatSub>
                </StatCell>
                <StatCell>
                  <StatLabel>MOIC / TVPI</StatLabel>
                  <StatValue>{tvpiCopy}</StatValue>
                  <StatSub>
                    {(metrics.tvpi ?? metrics.moic) != null
                      ? <>DPI {fmtMult(metrics.dpi)} · RVPI {fmtMult(metrics.rvpi)}</>
                      : <>realized + unrealized vs called</>}
                  </StatSub>
                </StatCell>
                <StatCell>
                  <StatLabel>KS-PME</StatLabel>
                  <StatValue>
                    {ksPmeCopy}
                    {metrics.ksPme != null && (
                      <Pill $tone={ksPmeTone}>{metrics.ksPme >= 1.0 ? 'OUTPERFORM' : 'UNDERPERFORM'}</Pill>
                    )}
                  </StatValue>
                  <StatSub>vs {metrics.benchmarkCode ?? 'benchmark'}</StatSub>
                </StatCell>
                <StatCell>
                  <StatLabel>Direct Alpha</StatLabel>
                  <StatValue>
                    {alphaCopy}
                    {metrics.directAlpha != null && (
                      <Pill $tone={alphaTone}>{alphaTone === 'pos' ? '▲' : alphaTone === 'neg' ? '▼' : '•'}</Pill>
                    )}
                  </StatValue>
                  <StatSub>annualized excess return</StatSub>
                </StatCell>
              </StatStrip>

              {tab === 'overview' && (
                <>
                  <Panel>
                    <PanelHead>
                      <PanelTitle>J-curve — capital deployment vs total value</PanelTitle>
                      <PanelMeta>
                        {buckets.length} monthly buckets
                        {jCurve?.latestNavDate ? ` · NAV ${fmtDate(jCurve.latestNavDate)}` : ''}
                      </PanelMeta>
                    </PanelHead>
                    <PanelBody>
                      {buckets.length === 0 ? (
                        <Empty>No cash-flow buckets — fund has no funded calls or received distributions yet.</Empty>
                      ) : (
                        <ChartFrame>
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={buckets} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                              <CartesianGrid stroke={portcoTokens.hairline} vertical={false} />
                              <XAxis
                                dataKey="month"
                                tickFormatter={monthLabel}
                                tick={{ fontSize: 11, fill: portcoTokens.inkSubtle }}
                                axisLine={{ stroke: portcoTokens.hairline }}
                                tickLine={false}
                                minTickGap={32}
                              />
                              <YAxis
                                tick={{ fontSize: 11, fill: portcoTokens.inkSubtle }}
                                tickFormatter={(v) => fmtMoney(v)}
                                axisLine={{ stroke: portcoTokens.hairline }}
                                tickLine={false}
                                width={70}
                              />
                              <Tooltip
                                formatter={(v: any) => fmtMoney(typeof v === 'number' ? v : Number(v))}
                                labelFormatter={(l) => monthLabel(String(l))}
                                contentStyle={{
                                  background: portcoTokens.surfaceBody,
                                  border: `1px solid ${portcoTokens.hairline}`,
                                  fontSize: 12,
                                }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              <ReferenceLine y={0} stroke={portcoTokens.inkSubtle} strokeDasharray="3 3" />
                              <Area
                                type="monotone"
                                dataKey="cumulativeCalled"
                                name="Cumulative called"
                                stroke={portcoTokens.statusBreached}
                                fill={portcoTokens.statusBreachedSubtle}
                                fillOpacity={0.6}
                              />
                              <Area
                                type="monotone"
                                dataKey="cumulativeDistributed"
                                name="Cumulative distributed"
                                stroke={portcoTokens.statusPassing}
                                fill={portcoTokens.statusPassingSubtle}
                                fillOpacity={0.6}
                              />
                              <Line
                                type="monotone"
                                dataKey="totalValue"
                                name="Total value (NAV + distributed)"
                                stroke={portcoTokens.accent}
                                strokeWidth={2}
                                dot={false}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </ChartFrame>
                      )}
                    </PanelBody>
                  </Panel>

                  <KpiSplit>
                    <Panel>
                      <PanelHead>
                        <PanelTitle>Capital deployment</PanelTitle>
                      </PanelHead>
                      <PanelBody>
                        <Table>
                          <tbody>
                            <Tr>
                              <Td>Committed</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMoney(metrics.committed)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>Called</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMoney(metrics.called)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>Distributed</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMoney(metrics.distributed)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>Reported NAV</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMoney(metrics.reportedNav)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>Total value</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMoney(metrics.totalValue)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>Unfunded</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMoney(metrics.unfunded)}</TdMono>
                            </Tr>
                          </tbody>
                        </Table>
                      </PanelBody>
                    </Panel>

                    <Panel>
                      <PanelHead>
                        <PanelTitle>Return decomposition</PanelTitle>
                      </PanelHead>
                      <PanelBody>
                        <Table>
                          <tbody>
                            <Tr>
                              <Td>IRR (since inception)</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtPct(metrics.irr)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>MOIC</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMult(metrics.moic)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>TVPI</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMult(metrics.tvpi)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>DPI</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMult(metrics.dpi)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>RVPI</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMult(metrics.rvpi)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>KS-PME</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtMult(metrics.ksPme)}</TdMono>
                            </Tr>
                            <Tr>
                              <Td>Direct Alpha</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtPct(metrics.directAlpha)}</TdMono>
                            </Tr>
                          </tbody>
                        </Table>
                      </PanelBody>
                    </Panel>
                  </KpiSplit>
                </>
              )}

              {tab === 'cashflows' && (
                <Panel>
                  <PanelHead>
                    <PanelTitle>Cash-flow timeline</PanelTitle>
                    <PanelMeta>
                      {metrics.cashFlowCount ?? 0} events ·
                      {metrics.firstFlowDate ? ` first ${fmtDate(metrics.firstFlowDate)}` : ''}
                      {metrics.lastFlowDate ? ` · last ${fmtDate(metrics.lastFlowDate)}` : ''}
                    </PanelMeta>
                  </PanelHead>
                  <PanelBody>
                    {cashFlowEvents.length === 0 ? (
                      <Empty>No cash flows recorded for this fund.</Empty>
                    ) : (
                      <>
                        <ChartFrame>
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={cashFlowEvents} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                              <CartesianGrid stroke={portcoTokens.hairline} vertical={false} />
                              <XAxis
                                dataKey="month"
                                tickFormatter={monthLabel}
                                tick={{ fontSize: 11, fill: portcoTokens.inkSubtle }}
                                axisLine={{ stroke: portcoTokens.hairline }}
                                tickLine={false}
                                minTickGap={32}
                              />
                              <YAxis
                                tick={{ fontSize: 11, fill: portcoTokens.inkSubtle }}
                                tickFormatter={(v) => fmtMoney(v)}
                                axisLine={{ stroke: portcoTokens.hairline }}
                                tickLine={false}
                                width={70}
                              />
                              <Tooltip
                                formatter={(v: any) => fmtMoney(typeof v === 'number' ? v : Number(v))}
                                labelFormatter={(l) => monthLabel(String(l))}
                                contentStyle={{
                                  background: portcoTokens.surfaceBody,
                                  border: `1px solid ${portcoTokens.hairline}`,
                                  fontSize: 12,
                                }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              <ReferenceLine y={0} stroke={portcoTokens.inkSubtle} />
                              <Bar dataKey="called" name="Calls (LP outflow)" fill={portcoTokens.statusBreached} />
                              <Bar dataKey="distributed" name="Distributions (LP inflow)" fill={portcoTokens.statusPassing} />
                              <Line
                                type="monotone"
                                dataKey="net"
                                name="Net period flow"
                                stroke={portcoTokens.accent}
                                strokeWidth={2}
                                dot={false}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </ChartFrame>

                        <Table style={{ marginTop: 16 }}>
                          <Thead>
                            <tr>
                              <Th>Month</Th>
                              <Th>Cumulative called</Th>
                              <Th>Cumulative distributed</Th>
                              <Th>NAV</Th>
                              <Th>Total value</Th>
                              <Th>DPI</Th>
                              <Th>TVPI</Th>
                            </tr>
                          </Thead>
                          <tbody>
                            {buckets.slice(-12).map((b) => (
                              <Tr key={b.month}>
                                <Td>{monthLabel(b.month)}</Td>
                                <TdMono>{fmtMoney(b.cumulativeCalled)}</TdMono>
                                <TdMono>{fmtMoney(b.cumulativeDistributed)}</TdMono>
                                <TdMono>{fmtMoney(b.reportedNav)}</TdMono>
                                <TdMono>{fmtMoney(b.totalValue)}</TdMono>
                                <TdMono>{fmtMult(b.dpi)}</TdMono>
                                <TdMono>{fmtMult(b.tvpi)}</TdMono>
                              </Tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </PanelBody>
                </Panel>
              )}

              {tab === 'benchmark' && (
                <Panel>
                  <PanelHead>
                    <PanelTitle>Public-market benchmark — {metrics.benchmarkCode ?? '—'}</PanelTitle>
                    <PanelMeta>
                      Five-variant PME suite · Kaplan-Schoar · Direct Alpha · Long-Nickels · PME+ · Cambridge mPME
                    </PanelMeta>
                  </PanelHead>
                  <PanelBody>
                    {/* Five-variant comparison table — leads the panel with the headline numbers. */}
                    <Table>
                      <thead>
                        <tr>
                          <Th>Variant</Th>
                          <Th>Author / year</Th>
                          <Th>Type</Th>
                          <Th style={{ textAlign: 'right' }}>Value</Th>
                          <Th>Verdict</Th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <Td><strong>KS-PME</strong></Td>
                          <Td>Kaplan & Schoar, 2005</Td>
                          <Td>Multiple</Td>
                          <Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {fmtMult(metrics.ksPme)}
                          </Td>
                          <Td>
                            {metrics.ksPme == null ? (
                              <Pill $tone="neutral">—</Pill>
                            ) : metrics.ksPme >= 1.0 ? (
                              <Pill $tone="pos">OUTPERFORM</Pill>
                            ) : (
                              <Pill $tone="neg">UNDERPERFORM</Pill>
                            )}
                          </Td>
                        </tr>
                        <tr>
                          <Td><strong>Cambridge mPME</strong></Td>
                          <Td>Cambridge Associates</Td>
                          <Td>Multiple</Td>
                          <Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {metrics.cambridgeMPme == null ? '—' : fmtMult(metrics.cambridgeMPme)}
                          </Td>
                          <Td>
                            {metrics.cambridgeMPme == null ? (
                              <Pill $tone="neutral">NAV history required</Pill>
                            ) : metrics.cambridgeMPme >= 1.0 ? (
                              <Pill $tone="pos">OUTPERFORM</Pill>
                            ) : (
                              <Pill $tone="neg">UNDERPERFORM</Pill>
                            )}
                          </Td>
                        </tr>
                        <tr>
                          <Td><strong>Direct Alpha</strong></Td>
                          <Td>Gredil-Sorensen-Jagannathan, 2014</Td>
                          <Td>IRR spread</Td>
                          <Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {fmtPct(metrics.directAlpha)}
                          </Td>
                          <Td>
                            {metrics.directAlpha == null ? (
                              <Pill $tone="neutral">—</Pill>
                            ) : metrics.directAlpha >= 0 ? (
                              <Pill $tone="pos">OUTPERFORM</Pill>
                            ) : (
                              <Pill $tone="neg">UNDERPERFORM</Pill>
                            )}
                          </Td>
                        </tr>
                        <tr>
                          <Td><strong>PME+</strong></Td>
                          <Td>Capital Dynamics / Rouvinez, 2003</Td>
                          <Td>IRR (NAV-anchored)</Td>
                          <Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {fmtPct(metrics.pmePlus)}
                            {metrics.pmePlusLambda != null ? (
                              <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 11 }}>
                                λ={metrics.pmePlusLambda.toFixed(3)}
                              </span>
                            ) : null}
                          </Td>
                          <Td>
                            {metrics.pmePlus == null || metrics.irr == null ? (
                              <Pill $tone="neutral">—</Pill>
                            ) : metrics.irr > metrics.pmePlus ? (
                              <Pill $tone="pos">OUTPERFORM</Pill>
                            ) : (
                              <Pill $tone="neg">UNDERPERFORM</Pill>
                            )}
                          </Td>
                        </tr>
                        <tr>
                          <Td><strong>Long-Nickels PME</strong></Td>
                          <Td>Long & Nickels, 1996</Td>
                          <Td>IRR (counterfactual)</Td>
                          <Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {fmtPct(metrics.longNickelsPme ?? metrics.benchmarkIRR)}
                          </Td>
                          <Td>
                            {(() => {
                              const ln = metrics.longNickelsPme ?? metrics.benchmarkIRR;
                              if (ln == null || metrics.irr == null) return <Pill $tone="neutral">—</Pill>;
                              return metrics.irr > ln
                                ? <Pill $tone="pos">OUTPERFORM</Pill>
                                : <Pill $tone="neg">UNDERPERFORM</Pill>;
                            })()}
                          </Td>
                        </tr>
                        <tr style={{ borderTop: '1px solid var(--portco-hairline, #e5e7eb)' }}>
                          <Td><strong>Fund IRR</strong> <span style={{ opacity: 0.6 }}>(reference)</span></Td>
                          <Td>—</Td>
                          <Td>IRR</Td>
                          <Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            <strong>{fmtPct(metrics.irr)}</strong>
                          </Td>
                          <Td>
                            <Pill $tone={irrTone}>baseline</Pill>
                          </Td>
                        </tr>
                      </tbody>
                    </Table>

                    {/* Method explainer — for allocators who want the full justification under each row. */}
                    <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--portco-hairline, #e5e7eb)' }}>
                      <PanelMeta style={{ marginBottom: 12 }}>Method detail</PanelMeta>
                      <InsightRow>
                        <InsightLabel>KS-PME</InsightLabel>
                        <InsightBody>
                          <strong>{fmtMult(metrics.ksPme)}</strong> — ratio of FV(distributions) to FV(contributions)
                          when each cash flow is grown at the {metrics.benchmarkCode} index return. The
                          industry-default multiple-based PME.
                        </InsightBody>
                      </InsightRow>
                      <InsightRow>
                        <InsightLabel>Cambridge mPME</InsightLabel>
                        <InsightBody>
                          {metrics.cambridgeMPme == null ? (
                            <>
                              Requires GP-reported NAV at each distribution date (sell-down ratio = distribution / NAV).
                              Will populate once a historical NAV time-series is available — until then we surface
                              KS-PME and PME+ as the multiple/IRR pair, and never fabricate.
                            </>
                          ) : (
                            <>
                              <strong>{fmtMult(metrics.cambridgeMPme)}</strong> — proportional sell-down avoids
                              KS-PME's terminal-NAV bias by scaling each distribution against then-current GP NAV.
                            </>
                          )}
                        </InsightBody>
                      </InsightRow>
                      <InsightRow>
                        <InsightLabel>Direct Alpha</InsightLabel>
                        <InsightBody>
                          <strong>{fmtPct(metrics.directAlpha)}</strong> — annualized excess return relative to
                          the {metrics.benchmarkCode} timing-matched counterfactual. Computed by re-running the
                          IRR engine on benchmark-scaled cash flows so both legs use the same money-weighted
                          convention.
                        </InsightBody>
                      </InsightRow>
                      <InsightRow>
                        <InsightLabel>PME+ (λ-anchored)</InsightLabel>
                        <InsightBody>
                          <strong>{fmtPct(metrics.pmePlus)}</strong>
                          {metrics.pmePlusLambda != null && (
                            <> with scaling λ = <strong>{metrics.pmePlusLambda.toFixed(3)}</strong></>
                          )}
                          {' '}— Capital Dynamics method that fixes Long-Nickels' "going short" pathology by
                          scaling distributions so the synthetic benchmark NAV at terminal exactly matches the
                          LP's reported NAV. λ &gt; 1 means the LP took distributions before the public market
                          would have allowed; λ &lt; 1 the opposite.
                        </InsightBody>
                      </InsightRow>
                      <InsightRow>
                        <InsightLabel>Long-Nickels PME</InsightLabel>
                        <InsightBody>
                          <strong>{fmtPct(metrics.longNickelsPme ?? metrics.benchmarkIRR)}</strong> — IRR of the
                          original 1996 public-market counterfactual: each contribution buys benchmark units,
                          each distribution sells them, residual liquidates at terminal index. Compare directly
                          with fund IRR <strong>{fmtPct(metrics.irr)}</strong>.
                        </InsightBody>
                      </InsightRow>
                      <InsightRow>
                        <InsightLabel>Spread (IRR − Long-Nickels)</InsightLabel>
                        <InsightBody>
                          <Pill $tone={alphaTone}>
                            {metrics.irr != null && (metrics.longNickelsPme ?? metrics.benchmarkIRR) != null
                              ? fmtPct(metrics.irr - (metrics.longNickelsPme ?? metrics.benchmarkIRR ?? 0))
                              : '—'}
                          </Pill>{' '}
                          Simple IRR difference. Differs from Direct Alpha because IRR ignores cash-flow
                          timing relative to the benchmark path; use Direct Alpha for the reweighted excess.
                        </InsightBody>
                      </InsightRow>
                    </div>

                    {/* Provenance footer — transparent about whether benchmark is licensed-real or composite-proxy. */}
                    {metrics.dataSource && (
                      <div
                        style={{
                          marginTop: 20,
                          paddingTop: 12,
                          borderTop: '1px solid var(--portco-hairline, #e5e7eb)',
                          fontSize: 11,
                          opacity: 0.7,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        Benchmark data:{' '}
                        <strong>
                          {metrics.dataSource.startsWith('PROXY_')
                            ? `Composite proxy (${metrics.dataSource})`
                            : metrics.dataSource.endsWith('_OFFICIAL')
                            ? `Licensed feed (${metrics.dataSource})`
                            : `Live ETF feed (${metrics.dataSource})`}
                        </strong>
                      </div>
                    )}
                  </PanelBody>
                </Panel>
              )}

              {tab === 'secondary' && (
                <Panel>
                  <PanelHead>
                    <PanelTitle>Secondary pricing — bid band on reported NAV</PanelTitle>
                    <PanelMeta>
                      Three-point bid (low / mid / high) — premium / market / deep-discount
                      · projects remaining calls + back-weighted distributions
                      · pricing only, not a buy/sell signal
                    </PanelMeta>
                  </PanelHead>
                  <PanelBody>
                    {priceError && <ErrorBanner>{priceError}</ErrorBanner>}

                    <GenForm
                      onSubmit={(e) => {
                        e.preventDefault();
                        fetchPriceBand();
                      }}
                    >
                      <FieldGroup>
                        Market discount rate (% p.a.)
                        <DateInput
                          type="number"
                          step="0.25"
                          min={0}
                          max={100}
                          placeholder="default 12.00"
                          value={discountInput}
                          onChange={(e) => setDiscountInput(e.target.value)}
                          style={{ width: 180 }}
                        />
                      </FieldGroup>
                      <GenerateButton type="submit" disabled={priceLoading}>
                        {priceLoading ? 'Pricing…' : 'Re-price'}
                      </GenerateButton>
                      <PanelMeta style={{ marginLeft: 'auto' }}>
                        Low = mid − 200 bps · High = mid + 400 bps
                      </PanelMeta>
                    </GenForm>

                    {priceLoading && !priceBand && <Loading>Pricing secondary…</Loading>}

                    {priceBand && (
                      <>
                        <BidGrid>
                          <BidCard $tone="low">
                            <BidLabel>Low (premium bid)</BidLabel>
                            <BidValue>{fmtMoney(priceBand.bidLow)}</BidValue>
                            <BidPct>
                              {priceBand.bidLowPctOfNav != null
                                ? `${priceBand.bidLowPctOfNav.toFixed(2)}% of NAV`
                                : '—'}
                            </BidPct>
                            <BidRate>
                              discount{' '}
                              {priceBand.discountRateLowPct != null
                                ? `${priceBand.discountRateLowPct.toFixed(2)}%`
                                : '—'}
                            </BidRate>
                          </BidCard>
                          <BidCard $tone="mid">
                            <BidLabel>Mid (market)</BidLabel>
                            <BidValue>{fmtMoney(priceBand.bidMid)}</BidValue>
                            <BidPct>
                              {priceBand.bidMidPctOfNav != null
                                ? `${priceBand.bidMidPctOfNav.toFixed(2)}% of NAV`
                                : '—'}
                            </BidPct>
                            <BidRate>
                              discount{' '}
                              {priceBand.discountRateMidPct != null
                                ? `${priceBand.discountRateMidPct.toFixed(2)}%`
                                : '—'}
                            </BidRate>
                          </BidCard>
                          <BidCard $tone="high">
                            <BidLabel>High (deep discount)</BidLabel>
                            <BidValue>{fmtMoney(priceBand.bidHigh)}</BidValue>
                            <BidPct>
                              {priceBand.bidHighPctOfNav != null
                                ? `${priceBand.bidHighPctOfNav.toFixed(2)}% of NAV`
                                : '—'}
                            </BidPct>
                            <BidRate>
                              discount{' '}
                              {priceBand.discountRateHighPct != null
                                ? `${priceBand.discountRateHighPct.toFixed(2)}%`
                                : '—'}
                            </BidRate>
                          </BidCard>
                        </BidGrid>

                        <AssumptionsGrid>
                          <AssumptionCell>
                            <AssumptionLabel>Reported NAV</AssumptionLabel>
                            <AssumptionValue>{fmtMoney(priceBand.reportedNav)}</AssumptionValue>
                          </AssumptionCell>
                          <AssumptionCell>
                            <AssumptionLabel>Called capital</AssumptionLabel>
                            <AssumptionValue>{fmtMoney(priceBand.calledCapital)}</AssumptionValue>
                          </AssumptionCell>
                          <AssumptionCell>
                            <AssumptionLabel>Distributed</AssumptionLabel>
                            <AssumptionValue>{fmtMoney(priceBand.distributedCapital)}</AssumptionValue>
                          </AssumptionCell>
                          <AssumptionCell>
                            <AssumptionLabel>Unfunded commitment</AssumptionLabel>
                            <AssumptionValue>{fmtMoney(priceBand.unfundedCommitment)}</AssumptionValue>
                          </AssumptionCell>
                          <AssumptionCell>
                            <AssumptionLabel>Residual life</AssumptionLabel>
                            <AssumptionValue>
                              {priceBand.residualMonths != null
                                ? `${priceBand.residualMonths} mo`
                                : '—'}
                            </AssumptionValue>
                          </AssumptionCell>
                          <AssumptionCell>
                            <AssumptionLabel>Investment period left</AssumptionLabel>
                            <AssumptionValue>
                              {priceBand.investmentMonthsRemaining != null
                                ? `${priceBand.investmentMonthsRemaining} mo`
                                : '—'}
                            </AssumptionValue>
                          </AssumptionCell>
                          <AssumptionCell>
                            <AssumptionLabel>Target MOIC remaining</AssumptionLabel>
                            <AssumptionValue>{fmtMult(priceBand.targetMoicRemaining)}</AssumptionValue>
                          </AssumptionCell>
                          <AssumptionCell>
                            <AssumptionLabel>As of</AssumptionLabel>
                            <AssumptionValue>{fmtDate(priceBand.asOfDate)}</AssumptionValue>
                          </AssumptionCell>
                        </AssumptionsGrid>

                        <ScopeNote>
                          Bid = PV(remaining distributions − remaining calls). Distributions
                          are projected as <strong>back-weighted</strong> across residual life
                          (10% / 30% / 60% across the three thirds), calls are linear over the
                          investment period remaining. Target MOIC is implied from the fund's
                          TVPI/RVPI history when available; otherwise a cohort fallback by
                          fund-type. <strong>This is pricing, not a recommendation</strong> —
                          buy/sell decisions remain with the LP / IC.
                        </ScopeNote>
                      </>
                    )}
                  </PanelBody>
                </Panel>
              )}

              {tab === 'subline' && fundId && (
                <SubscriptionLineTab fundId={Number(fundId)} />
              )}

              {tab === 'reports' && (
                <Panel>
                  <PanelHead>
                    <PanelTitle>ILPA v2.0 quarterly reports</PanelTitle>
                    <PanelMeta>
                      April 2024 template · 7 sub-templates · capital account · schedule of investments · fees · cash flow · performance · mgmt-fee offset · GP-led secondary
                    </PanelMeta>
                  </PanelHead>
                  <PanelBody>
                    {ilpaError && <ErrorBanner>{ilpaError}</ErrorBanner>}

                    <GenForm onSubmit={generateIlpaReport}>
                      <FieldGroup>
                        Quarter end
                        <DateField tone="light"
                          value={ilpaQuarterEnd ?? ''}
                          onChange={iso => setIlpaQuarterEnd(iso)}
                        />
                      </FieldGroup>
                      <GenerateButton type="submit" disabled={ilpaGenerating}>
                        {ilpaGenerating ? 'Generating…' : 'Generate quarter'}
                      </GenerateButton>
                      <PanelMeta style={{ marginLeft: 'auto' }}>
                        Pulls performance metrics + capital account state from IBOR; persists to <code>ilpa_quarterly_report</code> with bi-temporal close on prior open row.
                      </PanelMeta>
                    </GenForm>

                    <ReportsLayout>
                      <div>
                        <PanelMeta style={{ marginBottom: 8 }}>Filings history</PanelMeta>
                        {ilpaLoading && <Loading>Loading filings…</Loading>}
                        {!ilpaLoading && ilpaReports != null && ilpaReports.length === 0 && (
                          <Empty>No ILPA filings yet — generate the most recent quarter to seed.</Empty>
                        )}
                        {!ilpaLoading && ilpaReports != null && ilpaReports.length > 0 && (
                          <FilingsTableWrap>
                          <Table>
                            <Thead>
                              <Tr>
                                <Th>Quarter</Th>
                                <Th>Period</Th>
                                <Th style={{ textAlign: 'right' }}>IRR</Th>
                                <Th style={{ textAlign: 'right' }}>TVPI</Th>
                                <Th style={{ textAlign: 'right' }}>KS-PME</Th>
                                <Th>Knowledge from</Th>
                                <Th>Knowledge to</Th>
                                <Th>Source</Th>
                              </Tr>
                            </Thead>
                            <tbody>
                              {ilpaReports.map((row) => (
                                <ReportRow
                                  key={row.id}
                                  $active={ilpaSelectedId === row.id}
                                  onClick={() => openIlpaReport(row.id)}
                                >
                                  <Td>{row.quarter_end}</Td>
                                  <Td>{row.fiscal_period ?? '—'}</Td>
                                  <TdMono style={{ textAlign: 'right' }}>{fmtPct(row.irr ?? null)}</TdMono>
                                  <TdMono style={{ textAlign: 'right' }}>{fmtMult(row.tvpi ?? null)}</TdMono>
                                  <TdMono style={{ textAlign: 'right' }}>{fmtMult(row.ks_pme ?? null)}</TdMono>
                                  <TdMono>{fmtStamp(row.knowledge_from)}</TdMono>
                                  <TdMono>{fmtStamp(row.knowledge_to)}</TdMono>
                                  <Td>
                                    <span style={{ fontSize: 11, color: portcoTokens.inkSubtle }}>
                                      {row.data_source ?? '—'}
                                    </span>
                                  </Td>
                                </ReportRow>
                              ))}
                            </tbody>
                          </Table>
                          </FilingsTableWrap>
                        )}
                      </div>

                      <div>
                        <PanelMeta
                          style={{
                            marginBottom: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span>
                            {ilpaSelectedId
                              ? `Sub-template summary · report #${ilpaSelectedId}`
                              : 'Sub-template summary'}
                          </span>
                          {ilpaSelectedId && (
                            <button
                              type="button"
                              onClick={() => { setIlpaSelectedId(null); setIlpaSelectedReport(null); }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 11,
                                color: portcoTokens.inkSubtle,
                                textDecoration: 'underline',
                                padding: 0,
                              }}
                            >
                              Clear
                            </button>
                          )}
                        </PanelMeta>
                        {!ilpaSelectedId && (
                          <Empty>Select a filing above to inspect its 7 sub-templates.</Empty>
                        )}
                        {ilpaSelectedId && !ilpaSelectedReport && (
                          <Loading>Loading report detail…</Loading>
                        )}
                        {ilpaSelectedReport && (
                          <SubTplGrid>
                            {SUB_TEMPLATES.map((s) => {
                              const v = ilpaSelectedReport[s.key];
                              const summary = summarizeSubTemplate(v);
                              return (
                                <SubTplCard key={s.key}>
                                  <SubTplLabel>{s.label}</SubTplLabel>
                                  <SubTplCount>{summary}</SubTplCount>
                                  <SubTplRationale>{s.rationale}</SubTplRationale>
                                </SubTplCard>
                              );
                            })}
                          </SubTplGrid>
                        )}

                        {ilpaSelectedReport && (
                          <div
                            style={{
                              marginTop: 16,
                              fontSize: 11,
                              color: portcoTokens.inkSubtle,
                              fontFamily: portcoTokens.fontMono,
                            }}
                          >
                            template={String(ilpaSelectedReport.template ?? '—')} ·
                            {' '}benchmark={String(ilpaSelectedReport.benchmark ?? '—')} ·
                            {' '}dataSource={String(ilpaSelectedReport.dataSource ?? '—')}
                          </div>
                        )}
                      </div>
                    </ReportsLayout>
                  </PanelBody>
                </Panel>
              )}
            </>
          )}
        </>
  );

  // ---------- Render mode switch ----------
  // Embedded: dark breadcrumb bridge + light tearsheet, hosted inside the
  // Alternatives shell. No duplicate AxiomFlow brand row.
  if (embedded) {
    return (
      <>
        <TabularStyle />
        <EmbeddedShell className="lp-fund-tearsheet">
          <EmbeddedCrumbBar>
            <CrumbTrail>
              <span>Alternatives</span>
              <span className="sep">›</span>
              <span>GP Workbench</span>
              <span className="sep">›</span>
              <span>LP Performance</span>
              <span className="sep">›</span>
              <strong>{metrics?.label ?? 'Fund detail'}</strong>
            </CrumbTrail>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {metrics?.navAsOf && (
                <CrumbMeta>NAV as of {metrics.navAsOf}</CrumbMeta>
              )}
              <CrumbBack
                onClick={() =>
                  navigate('/portfolio-management?tab=alternatives&sub=lp-performance')
                }
              >
                ← LP Performance
              </CrumbBack>
            </div>
          </EmbeddedCrumbBar>
          <EmbeddedBody>{tearsheetBody}</EmbeddedBody>
        </EmbeddedShell>
      </>
    );
  }

  // Standalone (deep-link): preserves the original AxiomFlow-branded chrome
  // for shareable / external-system URLs.
  return (
    <>
      <PortcoGlobal />
      <TabularStyle />
      <Surface className="portco-surface lp-fund-tearsheet">
        <Header>
          <HeaderInner>
            <HeaderBrand>
              <HeaderBrandMark>AF</HeaderBrandMark>
              <span>AxiomFlow / LP Performance</span>
            </HeaderBrand>
            <HeaderCrumb>
              <BackLink onClick={() => navigate('/pe/performance')}>← All funds</BackLink>
              <span> / </span>
              <span>{metrics?.label ?? 'Fund'}</span>
            </HeaderCrumb>
          </HeaderInner>
        </Header>
        <Body>{tearsheetBody}</Body>
      </Surface>
    </>
  );
};

// ---------- ILPA sub-template summarizer ----------
function summarizeSubTemplate(v: any): string {
  if (v == null) return '—';
  if (Array.isArray(v)) return `${v.length} row${v.length === 1 ? '' : 's'}`;
  if (typeof v === 'object') {
    const keys = Object.keys(v);
    if (keys.length === 0) return 'empty';
    return `${keys.length} field${keys.length === 1 ? '' : 's'}`;
  }
  return String(v);
}

export default LPFundPerformanceDetailScreen;
